import { PageContainer } from "@/components/core/page-container";
import { ResponsiveImage } from "@/components/ui/responsive-image";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { getOptimizedImageAsset } from "@/lib/image-assets";
import { cn } from "@/lib/utils";
import type { ProcessSection as ProcessSectionContent } from "@/types/site";

interface ProcessSectionProps {
  content: ProcessSectionContent;
}

export function ProcessSection({ content }: ProcessSectionProps): JSX.Element {
  return (
    <section className="bg-surface-container-low py-20 sm:py-24 md:py-40">
      <PageContainer
        className={
          content.image
            ? "grid items-center gap-12 md:grid-cols-2 md:gap-24"
            : "flex flex-col items-center max-w-4xl mx-auto"
        }
      >
        <div className="flex w-full flex-col gap-10 sm:gap-12">
          <ScrollReveal className="space-y-0" distance="sm">
            {content.eyebrow ? (
              <p className="eyebrow mb-4">{content.eyebrow}</p>
            ) : null}
            <h2 className="max-w-3xl font-display text-[2.15rem] text-foreground sm:text-[2.5rem] md:text-[3rem]">
              {content.title}
            </h2>
          </ScrollReveal>

          <div className="process-timeline relative flex flex-col gap-10 border-l border-outline-variant/50 pl-5 sm:gap-12 sm:pl-6 md:gap-16 md:pl-8">
            {content.steps.map((step, index) => (
              <div
                key={step.title}
                className="process-step relative pl-4 sm:pl-5 md:pl-6"
                data-testid="process-step-item"
                style={{ animationDelay: `${String(index * 140)}ms` }}
              >
                <div
                  data-testid="process-step-marker"
                  className={cn(
                    "process-marker absolute left-[-1.05rem] top-[0.8rem] size-3.5 rounded-full border border-accent sm:left-[-1.3rem] sm:size-4 md:left-[-1.6rem]",
                    index === 0 ? "bg-accent" : "bg-[hsl(var(--surface))]",
                  )}
                  aria-hidden="true"
                  style={{ animationDelay: `${String(index * 240)}ms` }}
                />
                <h3 className="mb-3 font-display text-[1.6rem] text-foreground sm:text-[1.85rem] md:text-2xl">
                  {step.title}
                </h3>
                <p className="max-w-2xl text-base font-light leading-7 text-muted-foreground">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {content.image && (
          <ScrollReveal
            className="relative justify-self-center aspect-[4/5] w-full max-w-md sm:aspect-square md:h-[600px] md:max-w-lg md:aspect-auto"
            delay={220}
          >
            <div
              className="absolute inset-[-16px] border border-outline-variant/30 hidden md:block"
              aria-hidden="true"
            />
            <ResponsiveImage
              alt={content.title}
              containerClassName="absolute inset-0"
              height={getOptimizedImageAsset(content.image)?.height}
              imgClassName="absolute inset-0 h-full w-full object-cover"
              sizes="(min-width: 768px) 40vw, 100vw"
              sources={getOptimizedImageAsset(content.image)?.sources}
              src={content.image}
              width={getOptimizedImageAsset(content.image)?.width}
            />
          </ScrollReveal>
        )}
      </PageContainer>
    </section>
  );
}
